import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Revisa tu bandeja de entrada');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="orb orb-brand w-[600px] h-[600px] -top-[200px] -left-[200px] animate-float-slow"
          style={{ opacity: 0.4 }}
        />
        <div 
          className="orb orb-lavender w-[500px] h-[500px] -bottom-[150px] -right-[150px] animate-float-slower"
          style={{ opacity: 0.3, animationDelay: '2s' }}
        />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo-dark.png" 
              alt="FASCINAI ONE" 
              className="h-14 object-contain"
            />
          </div>
          <p className="text-white/60 text-lg">Recupera tu contraseña</p>
        </div>

        <div className="glass-dark rounded-2xl p-8 border border-white/5">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <p className="text-white/70 text-sm text-center mb-6">
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80 text-sm">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus:border-brand focus:ring-brand/20"
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-brand hover:bg-brand/90 text-black font-semibold rounded-xl transition-all duration-200"
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar enlace de recuperación'
                )}
              </Button>

              {/* Back to Login */}
              <div className="text-center pt-4">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 text-white/60 hover:text-brand transition-colors text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver al login
                </Link>
              </div>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                ¡Revisa tu email!
              </h3>
              <p className="text-white/60 text-sm mb-6">
                Si existe una cuenta con <strong className="text-white">{email}</strong>, 
                recibirás un enlace para restablecer tu contraseña.
              </p>
              <p className="text-white/40 text-xs mb-6">
                El enlace expira en 24 horas. Revisa también tu carpeta de spam.
              </p>
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-brand hover:text-brand/80 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
